// Customer Style Gaps — "which items has this customer NOT built yet", both ways:
//   BY CUSTOMER: pick a customer → browse/filter every eligible catalog item
//   missing from their assortment (same rules as the trusted Customer Styles
//   Excel tool, but interactive: thumbnails, stock, prices, filters, export).
//   BY ITEM: type a SKU → see exactly which of the ~104 CSL customers have
//   built it and which haven't.
//
// Backend: CustomerGapsController (api/CustomerGaps/*) — S3 inventory feeds +
// the CSL master sheet + ClickHouse catalog enrichment.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, CircularProgress,
  Skeleton, Alert, InputAdornment, Avatar, Table, TableHead, TableBody,
  TableRow, TableCell, LinearProgress, Collapse, Autocomplete,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdPersonSearch, MdInventory2, MdPlaylistAdd, MdDonutLarge, MdSearch,
  MdFileDownload, MdFilterListOff, MdExpandMore, MdExpandLess,
  MdCheckCircle, MdAddCircleOutline,
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS, apiCall } from '../config/api';

const ACCENT = '#0072B2';
const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const money = (n) => (Number(n) > 0 ? usd.format(Number(n)) : '—');
const count = (n) => num.format(Number(n) || 0);
const NUL = String.fromCharCode(0);
const clean = (v) => (v == null ? '' : String(v)).split(NUL).join('').trim();

const PAGE = 100;

function KpiTile({ icon: Icon, label, value, hint, color = ACCENT }) {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: color + '1a', color, flexShrink: 0 }}>
          <Icon size={17} />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: .3, textTransform: 'uppercase', fontSize: 10.5 }} noWrap>{label}</Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15, mt: 0.5 }} noWrap>{value}</Typography>
      {hint && <Typography variant="caption" sx={{ color: 'text.disabled' }} noWrap>{hint}</Typography>}
    </Paper>
  );
}

// =============================================================================
export default function CustomerStyleGaps() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const [mode, setMode] = useState('customer'); // 'customer' | 'item'
  const [error, setError] = useState('');

  // ---- shared roster ----------------------------------------------------------
  const [customers, setCustomers] = useState([]);
  const [cslAsOf, setCslAsOf] = useState('');
  useEffect(() => {
    let live = true;
    apiCall(API_ENDPOINTS.CUSTOMER_GAPS_CUSTOMERS)
      .then((r) => { if (live && r) { setCustomers(r.customers || []); setCslAsOf(r.cslAsOf || ''); } })
      .catch((e) => { if (live) setError(e.message || 'Failed to load customer list'); });
    return () => { live = false; };
  }, []);

  // ---- BY CUSTOMER state --------------------------------------------------------
  const [customer, setCustomer] = useState(null);   // {id, updated}
  const [gap, setGap] = useState(null);             // last response
  const [gapLoading, setGapLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [qText, setQText] = useState('');
  const [q, setQ] = useState('');
  const [collText, setCollText] = useState('');
  const [coll, setColl] = useState('');
  const [type, setType] = useState('');
  const [stock, setStock] = useState('all');
  const [sort, setSort] = useState('created');
  const [exporting, setExporting] = useState(false);
  const gapAbort = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(qText.trim()), 450);
    return () => clearTimeout(t);
  }, [qText]);
  useEffect(() => {
    const t = setTimeout(() => setColl(collText.trim()), 450);
    return () => clearTimeout(t);
  }, [collText]);

  const gapQs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    p.set('customer', customer?.id || '');
    if (q) p.set('q', q);
    if (coll) p.set('collection', coll);
    if (type) p.set('type', type);
    if (stock !== 'all') p.set('stock', stock);
    p.set('sort', sort);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [customer, q, coll, type, stock, sort]);

  // reset paging when inputs change
  useEffect(() => { setPage(0); }, [customer, q, coll, type, stock, sort]);

  useEffect(() => {
    if (!customer?.id) { setGap(null); return undefined; }
    // switching A -> B must not show A's KPIs/rows under B's name while B loads
    setGap((prev) => (prev && prev.customer !== customer.id ? null : prev));
    if (gapAbort.current) gapAbort.current.abort();
    const ac = new AbortController();
    gapAbort.current = ac;
    setGapLoading(true); setError('');
    apiCall(`${API_ENDPOINTS.CUSTOMER_GAPS_GAP}?${gapQs({ offset: page * PAGE, limit: PAGE })}`, { signal: ac.signal })
      .then((r) => { if (gapAbort.current === ac) { setGap(r); setGapLoading(false); } })
      .catch((e) => {
        if (e.name === 'AbortError' || gapAbort.current !== ac) return;
        setError(e.message || 'Gap lookup failed'); setGapLoading(false);
      });
    return () => ac.abort();
  }, [customer, gapQs, page]);

  const counts = gap?.counts || {};
  const coverage = Number(counts.eligible) > 0 ? (Number(counts.built) / Number(counts.eligible)) : null;
  const rows = gap?.rows || [];
  const totalFiltered = Number(counts.filtered) || 0;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE));

  // if the server-side gap shrank while we were deep in pagination, snap back in range
  useEffect(() => {
    if (!gapLoading && page > 0 && page + 1 > totalPages) setPage(totalPages - 1);
  }, [gapLoading, page, totalPages]);

  const exportExcel = async () => {
    if (!customer?.id) return;
    setExporting(true);
    try {
      // page through everything — one 50K-capped fetch silently truncates big gaps
      const CHUNK = 50000;
      const HARD_CAP = 400000;
      let all = [];
      let expected = Infinity;
      for (;;) {
        const r = await apiCall(`${API_ENDPOINTS.CUSTOMER_GAPS_GAP}?${gapQs({ offset: all.length, limit: CHUNK })}`);
        const got = (r && r.rows) || [];
        expected = Number(r?.counts?.filtered) || 0;
        all = all.concat(got);
        if (got.length < CHUNK || all.length >= expected || all.length >= HARD_CAP) break;
      }
      const head = ['ItemID', 'Created', 'Collection', 'Product Type', 'Color', 'Size', 'Style', 'Design',
        'ATS', 'Incoming', 'PO ETA', 'MAP', 'Wholesale', 'Description'];
      const body = all.map((x) => [clean(x.item), clean(x.created), clean(x.collection), clean(x.ptype),
        clean(x.color), clean(x.size), clean(x.style), clean(x.design),
        Number(x.atsq) || 0, Number(x.futq) || 0, clean(x.poEta), Number(x.map) || 0, Number(x.wsp) || 0, clean(x.descr)]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...body]), `Gaps ${customer.id}`.slice(0, 31));
      XLSX.writeFile(wb, `Style_Gaps_${customer.id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clearGapFilters = () => { setQText(''); setCollText(''); setType(''); setStock('all'); setSort('created'); };

  // ---- BY ITEM state -------------------------------------------------------------
  const [itemQText, setItemQText] = useState('');
  const [itemQ, setItemQ] = useState('');
  const [itemRes, setItemRes] = useState(null);
  const [itemLoading, setItemLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // item id whose chips are open
  useEffect(() => {
    const t = setTimeout(() => setItemQ(itemQText.trim()), 450);
    return () => clearTimeout(t);
  }, [itemQText]);
  useEffect(() => {
    if (itemQ.length < 2) { setItemRes(null); setItemLoading(false); return undefined; }
    let live = true;
    setItemLoading(true); setError('');
    apiCall(`${API_ENDPOINTS.CUSTOMER_GAPS_ITEM}?q=${encodeURIComponent(itemQ)}&limit=25`)
      .then((r) => { if (live) { setItemRes(r); setItemLoading(false); setExpanded(null); } })
      .catch((e) => { if (live) { setError(e.message || 'Item lookup failed'); setItemLoading(false); } });
    return () => { live = false; };
  }, [itemQ]);

  const cslCustomers = useMemo(() => (itemRes?.customers || []).map(clean), [itemRes]);

  // =============================================================================
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MdPersonSearch size={26} color={ACCENT} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Customer Style Gaps</Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            What each customer hasn't built yet — browse a customer's gap, or check any item across all customers
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setMode(v)}>
          <ToggleButton value="customer" sx={{ px: 1.5, textTransform: 'none', fontWeight: 700 }}>By Customer</ToggleButton>
          <ToggleButton value="item" sx={{ px: 1.5, textTransform: 'none', fontWeight: 700 }}>By Item</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ==================== BY CUSTOMER ==================== */}
      {mode === 'customer' && (
        <>
          <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
              <Autocomplete
                size="small" sx={{ width: 260 }} options={customers}
                getOptionLabel={(o) => o.id || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={customer}
                onChange={(e, v) => setCustomer(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{o.id}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>feed {o.updated}</Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Customer" placeholder="Pick a customer…" />}
              />
              <TextField size="small" placeholder="Search SKU / collection / color…" value={qText} onChange={(e) => setQText(e.target.value)} sx={{ width: 230 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
              <TextField size="small" placeholder="Collection…" value={collText} onChange={(e) => setCollText(e.target.value)} sx={{ width: 160 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
              <TextField size="small" select label="Product type" value={type} onChange={(e) => setType(e.target.value)} sx={{ width: 185 }}>
                <MenuItem value="">All types</MenuItem>
                {(gap?.types || []).map((t) => <MenuItem key={t.ptype} value={t.ptype}>{clean(t.ptype)} ({count(t.n)})</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Availability" value={stock} onChange={(e) => setStock(e.target.value)} sx={{ width: 160 }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="instock">In stock now</MenuItem>
                <MenuItem value="incoming">Arriving (on PO)</MenuItem>
                <MenuItem value="none">No supply yet</MenuItem>
              </TextField>
              <TextField size="small" select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ width: 155 }}>
                <MenuItem value="created">Newest first</MenuItem>
                <MenuItem value="stock">Most in stock</MenuItem>
                <MenuItem value="incoming">Most incoming</MenuItem>
                <MenuItem value="price">Highest MAP</MenuItem>
                <MenuItem value="collection">Collection A→Z</MenuItem>
                <MenuItem value="item">SKU A→Z</MenuItem>
              </TextField>
              {(q || coll || type || stock !== 'all') && (
                <MuiTooltip title="Clear filters">
                  <IconButton size="small" onClick={clearGapFilters}><MdFilterListOff /></IconButton>
                </MuiTooltip>
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" startIcon={exporting ? <CircularProgress size={14} /> : <MdFileDownload />}
                onClick={exportExcel} disabled={!customer || exporting || totalFiltered === 0}>
                Export gap list
              </Button>
            </Stack>
          </Paper>

          {!customer && (
            <Paper elevation={0} sx={{ p: 6, borderRadius: 3, border: `1px dashed ${theme.palette.divider}`, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>Pick a customer to see everything they haven't built</Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>{customers.length} customers with live inventory feeds</Typography>
            </Paper>
          )}

          {customer && (
            <>
              {/* KPIs */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 1.5, mb: 2 }}>
                {gapLoading && !gap
                  ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={98} sx={{ borderRadius: 3 }} />)
                  : (<>
                    <KpiTile icon={MdInventory2} label="Eligible Catalog" value={count(counts.eligible)} hint="active + sellable, non-exclusive" />
                    <KpiTile icon={MdCheckCircle} label="Built" value={count(counts.built)} hint={`in ${customer.id}'s feed`} color="#2e7d32" />
                    <KpiTile icon={MdPlaylistAdd} label="Not Built (gap)" value={count(counts.gap)} hint="the opportunity" color="#D55E00" />
                    <KpiTile icon={MdDonutLarge} label="Coverage" value={coverage == null ? '—' : `${(coverage * 100).toFixed(1)}%`} hint={`feeds: master ${gap?.freshness?.master || '?'} · ${customer.id} ${gap?.freshness?.customerFeed || '?'}`} color="#009E73" />
                  </>)}
              </Box>

              {/* results table */}
              <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {count(totalFiltered)} items{totalFiltered !== Number(counts.gap) ? ` (filtered from ${count(counts.gap)})` : ''} · page {page + 1} / {totalPages}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" disabled={page === 0 || gapLoading} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                  <Button size="small" disabled={page + 1 >= totalPages || gapLoading} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </Box>
                {gapLoading && <LinearProgress />}
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1000 }}>
                    <TableHead>
                      <TableRow>
                        {['', 'SKU', 'Collection', 'Type', 'Color', 'Size', 'ATS', 'Incoming', 'PO ETA', 'MAP', 'WSP', 'Added'].map((h) => (
                          <TableCell key={h || 'img'} sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', py: '9px' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!gapLoading && rows.length === 0 ? (
                        <TableRow><TableCell colSpan={12} sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                          {Number(counts.gap) === 0 ? `${customer.id} has built everything eligible 🎉` : 'No gap items match these filters'}
                        </TableCell></TableRow>
                      ) : (
                        rows.map((r) => (
                          <TableRow key={r.item} hover sx={{ '&:nth-of-type(even)': { bgcolor: theme.palette.action.hover } }}>
                            <TableCell sx={{ width: 46, py: '4px' }}>
                              <Avatar variant="rounded" src={clean(r.thumb) || undefined}
                                sx={{ width: 36, height: 36, bgcolor: dark ? '#26263a' : '#eef2f7', color: ACCENT, fontSize: 10, fontWeight: 800 }}>
                                {clean(r.item).slice(0, 2)}
                              </Avatar>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>{clean(r.item)}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{clean(r.collection)}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{clean(r.ptype)}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{clean(r.color)}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{clean(r.size)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', fontWeight: Number(r.atsq) > 0 ? 700 : 400, color: Number(r.atsq) > 0 ? '#2e7d32' : 'inherit' }}>{count(r.atsq)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{count(r.futq)}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{clean(r.poEta) || '—'}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{money(r.map)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{money(r.wsp)}</TableCell>
                            <TableCell sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{clean(r.created)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 2, textAlign: 'center' }}>
                Gap = master feed (discontinued kept only with ATS ≥ 3) − exclusive items − {customer.id}'s inventory feed − digit-prefixed SKUs · attributes &amp; stock from the warehouse catalog · same rules as the Customer Styles Excel tool
              </Typography>
            </>
          )}
        </>
      )}

      {/* ==================== BY ITEM ==================== */}
      {mode === 'item' && (
        <>
          <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <TextField size="small" autoFocus placeholder="Type a SKU (2+ chars), e.g. CY8521…" value={itemQText} onChange={(e) => setItemQText(e.target.value)} sx={{ width: 300 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
              {itemLoading && <CircularProgress size={18} />}
              {itemRes && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {count(itemRes.totalMatches)} matching items{Number(itemRes.totalMatches) > (itemRes.items || []).length ? ` — showing first ${(itemRes.items || []).length}` : ''} · CSL as of {itemRes.asOf}
                </Typography>
              )}
            </Stack>
          </Paper>

          {!itemRes && !itemLoading && (
            <Paper elevation={0} sx={{ p: 6, borderRadius: 3, border: `1px dashed ${theme.palette.divider}`, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>Search any SKU to see which customers have built it — and which haven't</Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>from the Customer Style List master sheet{cslAsOf ? ` (as of ${cslAsOf})` : ''}</Typography>
            </Paper>
          )}

          {itemRes && (itemRes.items || []).map((it) => {
            const notBuiltIdx = it.notBuiltIdx || [];
            const notBuiltSet = new Set(notBuiltIdx);
            const total = cslCustomers.length;
            const built = Number(it.builtCount) || 0;
            const open = expanded === it.item;
            return (
              <Paper key={it.item} elevation={0} sx={{ mb: 1.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                <Box onClick={() => setExpanded(open ? null : it.item)}
                  sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { bgcolor: theme.palette.action.hover } }}>
                  <Avatar variant="rounded" src={clean(it.thumb) || undefined}
                    sx={{ width: 44, height: 44, bgcolor: dark ? '#26263a' : '#eef2f7', color: ACCENT, fontSize: 11, fontWeight: 800 }}>
                    {clean(it.item).slice(0, 2)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 14 }} noWrap>{clean(it.item)}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>
                      {[clean(it.collection), clean(it.size), clean(it.color)].filter(Boolean).join(' · ') || '—'}
                      {clean(it.firstReceived) ? ` · first received ${clean(it.firstReceived)}` : ''}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                      <Box sx={{ flex: '0 0 160px', height: 6, borderRadius: 3, bgcolor: theme.palette.action.hover, overflow: 'hidden' }}>
                        <Box sx={{ width: `${total > 0 ? (built / total) * 100 : 0}%`, height: '100%', bgcolor: '#2e7d32' }} />
                      </Box>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        built by {built}/{total}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#D55E00', fontWeight: 700 }}>
                        · {total - built} to go
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(it.map)}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>ATS {count(it.atsq)} · inc {count(it.futq)}</Typography>
                  </Stack>
                  {open ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                </Box>
                <Collapse in={open} unmountOnExit>
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#D55E00', mb: 0.5 }}>
                      <MdAddCircleOutline size={12} style={{ verticalAlign: '-2px' }} /> Not built ({total - built})
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.25 }}>
                      {cslCustomers.map((c, k) => notBuiltSet.has(k) ? (
                        <Chip key={c} size="small" label={c} variant="outlined"
                          sx={{ height: 20, fontSize: 10.5, fontWeight: 600, color: '#D55E00', borderColor: '#D55E00' }} />
                      ) : null)}
                      {total - built === 0 && <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>everyone has it</Typography>}
                    </Stack>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#2e7d32', mb: 0.5 }}>
                      <MdCheckCircle size={12} style={{ verticalAlign: '-2px' }} /> Built ({built})
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {cslCustomers.map((c, k) => !notBuiltSet.has(k) ? (
                        <Chip key={c} size="small" label={c}
                          sx={{ height: 20, fontSize: 10.5, fontWeight: 600, color: '#fff', bgcolor: '#2e7d32' }} />
                      ) : null)}
                      {built === 0 && <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>nobody yet</Typography>}
                    </Stack>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}

          {itemRes && (itemRes.items || []).length === 0 && (
            <Paper elevation={0} sx={{ p: 5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.disabled' }}>No items match "{itemQ}" in the CSL master sheet</Typography>
            </Paper>
          )}

          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 2, textAlign: 'center' }}>
            Built / not built comes from the Customer Style List master sheet — a periodic snapshot{cslAsOf ? ` (currently as of ${cslAsOf})` : ''}; a customer's live feed may be slightly ahead of it
          </Typography>
        </>
      )}
    </Box>
  );
}
